import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, BookOpen, Compass, Landmark, Loader2, Award, CheckCircle2 } from "lucide-react";
import Reveal from "../components/Reveal";
import SaveButton from "../components/SaveButton";
import { useAuth } from "../context/AuthContext";

/**
 * AdventureDetail — renders one adventure as a long pop-up-book scroll.
 * Hero prose → Act I, II, III, IV — each with its narrative beat, its
 * "Chapters to read here" strip (resolved live from KB01), and the
 * embedded Computing-in-India historical beat.
 */
export default function AdventureDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [adv, setAdv] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setAdv(null);
    api.get(`/adventures/${slug}`).then(({ data }) => { if (!cancelled) setAdv(data); })
      .catch(() => { if (!cancelled) setAdv({ notfound: true }); });
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (!user) { setCompleted(false); return; }
    api.get(`/adventures/progress/me`).then(({ data }) => {
      setCompleted(!!(data.completions || []).find((c) => c.slug === slug));
    }).catch(() => {});
  }, [user, slug]);

  const onComplete = async () => {
    if (!user) {
      toast.info("Sign in to keep your certificates.");
      return;
    }
    setCompleting(true);
    try {
      await api.post(`/adventures/${slug}/complete`);
      setCompleted(true);
      toast.success("You've earned a certificate — open it below.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't record completion.");
    } finally {
      setCompleting(false);
    }
  };

  if (!adv) return <div className="container-narrow py-24 text-center text-ink2"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>;
  if (adv.notfound) {
    return (
      <div className="container-narrow py-24 text-center" data-testid="adventure-404">
        <h1 className="font-heading text-3xl font-semibold text-ink mb-4">That adventure hasn't been written yet.</h1>
        <Link to="/adventures" className="btn-outline-ink text-sm inline-flex items-center gap-1.5"><ArrowLeft className="w-4 h-4" /> All adventures</Link>
      </div>
    );
  }

  return (
    <div className="container-narrow py-10 md:py-14" data-testid={`adventure-${adv.slug}`}>
      <Link to="/adventures" className="inline-flex items-center gap-1.5 text-sm text-ink2 hover:text-ink mb-6">
        <ArrowLeft className="w-4 h-4" /> All adventures
      </Link>

      {/* Hero */}
      <Reveal className="mb-12">
        <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-marigold mb-2">{adv.kicker}</div>
        <h1 className={`font-heading text-5xl md:text-7xl font-semibold ${adv.cover_accent || "text-ink"} leading-[1.02] mb-5`}>
          {adv.title}
        </h1>
        <p className="text-lg md:text-xl text-ink2 italic leading-relaxed max-w-2xl border-l-4 border-marigold pl-5">
          {adv.hero}
        </p>
        <div className="mt-3 font-mono text-xs text-ink2">
          {adv.acts.length} acts · {adv.acts.reduce((n, a) => n + (a.chapters?.length || 0), 0)} chapters from the curriculum
        </div>
      </Reveal>

      {/* Acts */}
      <div className="space-y-14">
        {adv.acts.map((act, i) => (
          <Reveal key={act.n} delay={(i % 4) + 1}>
            <ActBlock act={act} />
          </Reveal>
        ))}
      </div>

      {/* Outro */}
      {adv.outro && (
        <Reveal className="mt-16 rounded-3xl border-2 border-ink bg-cream shadow-[8px_8px_0_0_#1F1A17] p-8 md:p-10">
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-marigold mb-3">Curtain</div>
          <p className="font-heading text-2xl md:text-3xl text-ink italic leading-relaxed" data-testid="adventure-outro">{adv.outro}</p>
        </Reveal>
      )}

      {/* Completion + Certificate — appears above prev/next nav */}
      <Reveal className="mt-14">
        <div
          className={`rounded-3xl border-2 border-ink shadow-[8px_8px_0_0_#1F1A17] p-7 md:p-8 flex items-center gap-5 flex-wrap ${completed ? "bg-marigold/[0.08]" : "bg-cream"}`}
          data-testid="adventure-complete-card"
        >
          <div className={`w-14 h-14 rounded-2xl border-2 border-ink flex items-center justify-center flex-shrink-0 ${completed ? "bg-marigold text-cream" : "bg-cream text-marigold"}`}>
            {completed ? <CheckCircle2 className="w-6 h-6" /> : <Award className="w-6 h-6" />}
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-marigold mb-1">
              {completed ? "Adventure complete" : "Finished reading?"}
            </div>
            <h3 className="font-heading text-2xl font-semibold text-ink leading-tight">
              {completed ? "Your certificate is ready." : "Mark this adventure complete and earn a printable certificate."}
            </h3>
            <p className="text-sm text-ink2 mt-1">
              {completed
                ? "A pop-up-book certificate with the chapters you walked, Ms. Pradhan's signature, and a little map of India."
                : "Mark complete when you've read the chapters that walked with each act."}
            </p>
          </div>
          {completed ? (
            <Link
              to={`/adventures/${slug}/certificate`}
              className="btn-primary text-sm inline-flex items-center gap-1.5"
              data-testid="adventure-view-certificate"
            >
              <Award className="w-4 h-4" /> Open certificate <ArrowRight className="w-4 h-4" />
            </Link>
          ) : (
            <button
              onClick={onComplete}
              disabled={completing || !user}
              className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
              data-testid="adventure-mark-complete"
            >
              {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
              {user ? "Mark adventure complete" : "Sign in to earn certificate"}
            </button>
          )}
        </div>
      </Reveal>

      <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
        <Link to="/adventures" className="btn-outline-ink text-sm inline-flex items-center gap-1.5">
          <ArrowLeft className="w-4 h-4" /> More adventures
        </Link>
        <Link to="/learn" className="btn-primary text-sm inline-flex items-center gap-1.5">
          The twelve worlds <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function ActBlock({ act }) {
  return (
    <section className="rounded-3xl border-2 border-ink bg-cream shadow-[8px_8px_0_0_#1F1A17] overflow-hidden" data-testid={`adventure-act-${act.n}`}>
      <div className="p-7 md:p-10 relative">
        <div className="absolute top-4 right-6 font-heading text-[10rem] font-semibold text-ink/[0.045] leading-none select-none pointer-events-none">
          {act.n}
        </div>

        <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-marigold mb-1 relative">
          Act {act.n} · {act.eyebrow}
        </div>
        <h2 className="font-heading text-3xl md:text-4xl font-semibold text-ink leading-tight mb-5 relative">
          {act.title}
        </h2>

        <p className="text-ink leading-[1.75] text-base md:text-lg mb-6 relative whitespace-pre-line max-w-3xl">
          {act.prose}
        </p>

        {/* India beat */}
        {act.india_beat && (
          <div className="mb-6 rounded-2xl border-2 border-marigold/40 bg-marigold/[0.06] p-5 flex items-start gap-4 relative" data-testid={`adventure-india-${act.n}`}>
            <div className="w-9 h-9 rounded-lg bg-marigold/25 text-marigold flex items-center justify-center flex-shrink-0">
              <Landmark className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-marigold mb-1">India · the parallel history</div>
              <p className="text-ink leading-relaxed italic">{act.india_beat}</p>
            </div>
          </div>
        )}

        {/* Chapters for this act */}
        {act.chapters && act.chapters.length > 0 && (
          <div className="mt-6 relative" data-testid={`adventure-chapters-${act.n}`}>
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-marigold mb-3 inline-flex items-center gap-2">
              <BookOpen className="w-3.5 h-3.5" /> Chapters to read alongside this act
            </div>
            <div className="rounded-2xl border-2 border-ink overflow-hidden">
              <ol className="divide-y divide-ink/10">
                {act.chapters.map((c) => (
                  <li key={c.slug} className="hover:bg-ink/[0.04] transition">
                    <div className="flex items-start px-4 md:px-5 py-3.5 gap-3">
                      <Link to={`/library/${c.slug}`} className="flex-1 min-w-0 group" data-testid={`adventure-chapter-${act.n}-${c.code || c.slug}`}>
                        <div className="text-[10px] font-mono font-bold text-ink/50 mb-0.5">{c.code}</div>
                        <div className="font-heading text-base font-semibold text-ink group-hover:text-marigold transition leading-snug">
                          {c.title}
                        </div>
                        {c.summary && (
                          <div className="text-xs text-ink2 mt-1 line-clamp-2">{c.summary}</div>
                        )}
                      </Link>
                      <div className="pt-0.5 flex items-center gap-2 flex-shrink-0">
                        <SaveButton slug={c.slug} title={c.title} size="sm" testId={`adventure-save-${c.code || c.slug}`} />
                        <ArrowRight className="w-4 h-4 text-ink/40" />
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
